import { useCallback } from 'react'
import { toast } from 'sonner'
import { useLanguage } from '@/lib/i18n/language-provider'
import { getPortColor, getOutletColor } from '../constants'
import {
  appendMireDataConnection,
  appendMirePowerConnection,
  getMireCellCenter,
  type DataPageWiringPatch,
  type PowerPageWiringPatch
} from '../utils/cabling-mutations'
import type {
  WorkspaceElement, VirtualMireCell, MireConnection, MirePowerConnection,
  DataPage, PowerPage
} from '@/types/synoptique'

export interface MireCellRef {
  imageId: string
  row: number
  col: number
}

interface UseMireGridParams {
  workspaceElements: WorkspaceElement[]
  mireConnections: MireConnection[]
  mirePowerConnections: MirePowerConnection[]
  readOnly: boolean
  /** Sélections lues via refs → toujours fraîches, même en navigation clavier rapide */
  getDataSelection: () => { processorId: string; port: number } | null
  getPowerSelection: () => { cabinetId: string; outlet: number } | null
  /** Mutations atomiques : la fonction reçoit la page cible (état le plus récent)
   *  et retourne le patch à appliquer, ou null pour un no-op (cellule déjà connectée, etc.) */
  applyDataPageWiring: (fn: (page: DataPage) => DataPageWiringPatch | null) => void
  applyPowerPageWiring: (fn: (page: PowerPage) => PowerPageWiringPatch | null) => void
  setCurrentMireCell: (cell: MireCellRef | null) => void
  setSkipNextHistorySaveRef: React.MutableRefObject<(v: boolean) => void>
  saveToHistoryImmediateRef: React.MutableRefObject<(desc: string) => void>
  onboarding: { state: { isActive: boolean }; completeAction: (action: string, count: number) => void }
}

/** Calculate virtual grid cells from mire image (pure function) */
export function getVirtualCellsFromImage(imageElement: WorkspaceElement): VirtualMireCell[] {
  const mireConfig = imageElement.data?.mireConfig
  if (!mireConfig) {
    return []
  }

  const ledWallMode = mireConfig.ledWallMode
  if (!ledWallMode) {
    return []
  }

  const { columns, rows } = ledWallMode

  if (!columns || !rows || columns <= 0 || rows <= 0) {
    return []
  }

  const cellWidth = imageElement.size.width / columns
  const cellHeight = imageElement.size.height / rows

  const cells: VirtualMireCell[] = []

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < columns; col++) {
      const cell: VirtualMireCell = {
        imageId: imageElement.id,
        row: row + 1,
        col: col + 1,
        centerX: imageElement.position.x + (col + 0.5) * cellWidth,
        centerY: imageElement.position.y + (row + 0.5) * cellHeight,
        width: cellWidth,
        height: cellHeight
      }
      cells.push(cell)
    }
  }

  return cells
}

/** Get a specific virtual cell by row/col (pure function) */
export function getVirtualCell(
  imageElement: WorkspaceElement,
  row: number,
  col: number
): VirtualMireCell | null {
  const center = getMireCellCenter(imageElement, row, col)
  if (!center) return null
  const mireConfig = imageElement.data?.mireConfig
  const ledWallMode = mireConfig?.ledWallMode
  const columns = ledWallMode?.columns || 1
  const rows = ledWallMode?.rows || 1
  return {
    imageId: imageElement.id,
    row,
    col,
    centerX: center.x,
    centerY: center.y,
    width: imageElement.size.width / columns,
    height: imageElement.size.height / rows
  }
}

/** Find closest unconnected mire cell in a direction (pure function) */
export function findClosestMireCellInDirection(
  currentCell: { imageId: string; row: number; col: number },
  direction: 'up' | 'down' | 'left' | 'right',
  allCells: VirtualMireCell[],
  isConnected: (imageId: string, row: number, col: number) => boolean
): { row: number; col: number } | null {
  const currentVirtualCell = allCells.find(c =>
    c.row === currentCell.row && c.col === currentCell.col
  )

  if (!currentVirtualCell) return null

  let closestCell: VirtualMireCell | null = null
  let minDistance = Infinity

  allCells.forEach(cell => {
    if (cell.row === currentCell.row && cell.col === currentCell.col) return
    if (isConnected(cell.imageId, cell.row, cell.col)) return

    const dx = cell.centerX - currentVirtualCell.centerX
    const dy = cell.centerY - currentVirtualCell.centerY
    const distance = Math.sqrt(dx * dx + dy * dy)

    const isInDirection =
      (direction === 'right' && dx > 0 && Math.abs(dy) < Math.abs(dx)) ||
      (direction === 'left' && dx < 0 && Math.abs(dy) < Math.abs(dx)) ||
      (direction === 'down' && dy > 0 && Math.abs(dx) < Math.abs(dy)) ||
      (direction === 'up' && dy < 0 && Math.abs(dx) < Math.abs(dy))

    if (isInDirection && distance < minDistance) {
      closestCell = cell
      minDistance = distance
    }
  })

  return closestCell ? { row: (closestCell as VirtualMireCell).row, col: (closestCell as VirtualMireCell).col } : null
}

export function useMireGrid({
  workspaceElements,
  mireConnections,
  mirePowerConnections,
  readOnly,
  getDataSelection,
  getPowerSelection,
  applyDataPageWiring,
  applyPowerPageWiring,
  setCurrentMireCell,
  setSkipNextHistorySaveRef,
  saveToHistoryImmediateRef,
  onboarding,
}: UseMireGridParams) {
  const { t } = useLanguage()

  const isCellConnected = useCallback((
    imageId: string,
    row: number,
    col: number,
    mode: 'data' | 'power' = 'data'
  ): boolean => {
    if (mode === 'data') {
      return mireConnections.some(c =>
        c.imageId === imageId &&
        c.gridPosition.row === row &&
        c.gridPosition.col === col
      )
    } else {
      return mirePowerConnections.some(c =>
        c.imageId === imageId &&
        c.gridPosition.row === row &&
        c.gridPosition.col === col
      )
    }
  }, [mireConnections, mirePowerConnections])

  const getCellConnection = useCallback((
    imageId: string,
    row: number,
    col: number,
    mode: 'data' | 'power' = 'data'
  ): MireConnection | MirePowerConnection | null => {
    if (mode === 'data') {
      return mireConnections.find(c =>
        c.imageId === imageId &&
        c.gridPosition.row === row &&
        c.gridPosition.col === col
      ) || null
    } else {
      return mirePowerConnections.find(c =>
        c.imageId === imageId &&
        c.gridPosition.row === row &&
        c.gridPosition.col === col
      ) || null
    }
  }, [mireConnections, mirePowerConnections])

  const handleMireCellClickForData = useCallback((
    imageId: string,
    row: number,
    col: number
  ) => {
    if (readOnly) return

    const selection = getDataSelection()
    if (!selection) {
      toast.error(t('synoptic.toast.selectProcessorFirst'))
      return
    }

    // Vérifications UX sur l'état de rendu (toasts) — l'invariant réel est
    // appliqué dans l'updater atomique ci-dessous.
    const imageElement = workspaceElements.find(el =>
      el.id === imageId && el.type === 'image'
    )

    if (!imageElement) {
      toast.error(t('synoptic.toast.mireNotFound'))
      return
    }

    if (isCellConnected(imageId, row, col, 'data')) {
      toast.warning(t('synoptic.toast.cellAlreadyConnected'))
      return
    }

    if (!getVirtualCell(imageElement, row, col)) {
      toast.error(t('synoptic.toast.invalidCell').replace('{row}', String(row)).replace('{col}', String(col)))
      return
    }

    setSkipNextHistorySaveRef.current(true)

    applyDataPageWiring(page => appendMireDataConnection(page, {
      imageId,
      row,
      col,
      processorId: selection.processorId,
      port: selection.port,
      color: getPortColor(selection.port)
    }))
    setCurrentMireCell({ imageId, row, col })

    if (onboarding.state.isActive) {
      onboarding.completeAction('panels-cabled', mireConnections.length + 1)
    }

    setTimeout(() => {
      setSkipNextHistorySaveRef.current(false)
      saveToHistoryImmediateRef.current(`Cellule mire [${row},${col}] connectée`)
    }, 100)

  }, [
    getDataSelection,
    workspaceElements,
    mireConnections,
    isCellConnected,
    readOnly,
    applyDataPageWiring,
    setCurrentMireCell,
    setSkipNextHistorySaveRef,
    saveToHistoryImmediateRef,
    onboarding,
    t,
  ])

  const handleMireCellClickForPower = useCallback((
    imageId: string,
    row: number,
    col: number
  ) => {
    if (readOnly) return

    const selection = getPowerSelection()
    if (!selection) {
      toast.error(t('synoptic.toast.selectCabinetFirst'))
      return
    }

    const imageElement = workspaceElements.find(el =>
      el.id === imageId && el.type === 'image'
    )

    if (!imageElement) {
      toast.error(t('synoptic.toast.mireNotFound'))
      return
    }

    if (isCellConnected(imageId, row, col, 'power')) {
      toast.warning(t('synoptic.toast.cellAlreadyPowered'))
      return
    }

    if (!getVirtualCell(imageElement, row, col)) {
      toast.error(t('synoptic.toast.invalidCell').replace('{row}', String(row)).replace('{col}', String(col)))
      return
    }

    setSkipNextHistorySaveRef.current(true)

    applyPowerPageWiring(page => appendMirePowerConnection(page, {
      imageId,
      row,
      col,
      cabinetId: selection.cabinetId,
      outlet: selection.outlet,
      color: getOutletColor(selection.outlet)
    }))
    setCurrentMireCell({ imageId, row, col })

    if (onboarding.state.isActive) {
      onboarding.completeAction('panels-cabled', mirePowerConnections.length + 1)
    }

    setTimeout(() => {
      setSkipNextHistorySaveRef.current(false)
      saveToHistoryImmediateRef.current(`Cellule mire [${row},${col}] alimentée`)
    }, 100)

  }, [
    getPowerSelection,
    workspaceElements,
    mirePowerConnections,
    isCellConnected,
    readOnly,
    applyPowerPageWiring,
    setCurrentMireCell,
    setSkipNextHistorySaveRef,
    saveToHistoryImmediateRef,
    onboarding,
    t,
  ])

  return {
    getVirtualCellsFromImage,
    getVirtualCell,
    isCellConnected,
    getCellConnection,
    handleMireCellClickForData,
    handleMireCellClickForPower,
    findClosestMireCellInDirection,
  }
}
